import { attributionTimeBuckets } from "./attributionStamp";
import { extractDates, parseSoleSize } from "./figures";
import type { EntryKind } from "./qualifyLead";

/**
 * U1 RESEARCH (Agent Brains 5.1 L2, design Q21). Deterministic signals read off
 * one inbound DM. Pure: the message text is read in memory and never leaves
 * this function. What comes back is codes and buckets, so the research card
 * (`researchCard.ts`) can hold it without holding a word the customer typed.
 *
 * POSITIVE ONLY (Q21). A specificity code says the message NAMED something. Its
 * absence means unknown, never "vague": a short message is not a vague buyer,
 * and a card that said so would spend trust on a guess (CLAUDE.md section 0).
 *
 * Dates and sizes are read through `figures.ts` (`extractDates`,
 * `parseSoleSize`), because the figure chokepoint tests forbid a second parser
 * (F-16). Timing uses `attributionTimeBuckets`, so a time is only ever a day
 * part and a weekend flag.
 */

export const SPECIFICITY_CODES = ["spec.piece_named", "spec.date_named", "spec.size_named"] as const;
export type SpecificityCode = (typeof SPECIFICITY_CODES)[number];

// The entry kinds are qualifyLead.ts (EntryKind), the vocabulary U3 reads; an unknown channel is null.

export interface DmSignals {
  /** In catalogue order, distinct. Empty means nothing was named, which is unknown. */
  readonly specificity: readonly SpecificityCode[];
  /** Day part and weekend flag in the shop's zone, or null when no zone is known. */
  readonly timing: { readonly dayPart: string; readonly weekend: boolean } | null;
  readonly entryKind: EntryKind | null;
}

export interface DmSignalsInput {
  /** The latest customer message. Read here, never returned. */
  readonly text: string;
  readonly sentAt: Date;
  readonly channel: string;
  /** `threads.entry_source`: which post, reel or story opened the thread. */
  readonly entrySource: string | null;
  /** The shop's IANA zone, from the gateway's budget day zone. Null: no timing code. */
  readonly shopTimeZone: string | null;
}

/**
 * A named piece is a category word AND a descriptor of the piece (a stone, a
 * shape, a metal or a setting) in the same message. "a ring" alone names a
 * category, not a piece, so it earns no code. AWAITING RATIFICATION: both
 * closed word lists.
 */
const PIECE_WORD =
  /\b(?:rings?|bands?|earrings?|studs?|hoops?|necklaces?|pendants?|bracelets?|bangles?|watch(?:es)?|chains?|solitaires?)\b/i;
const PIECE_DESCRIPTOR =
  /\b(?:oval|round|princess|emerald|pear|cushion|marquise|radiant|asscher|heart|diamond|sapphire|ruby|pearl|opal|gold|platinum|silver|solitaire|halo|pave|eternity|tennis|three[- ]stone|bezel|signet|cuban|rope)\b/i;

export function readDmSignals(input: DmSignalsInput): DmSignals {
  const specificity: SpecificityCode[] = [];
  if (PIECE_WORD.test(input.text) && PIECE_DESCRIPTOR.test(input.text)) specificity.push("spec.piece_named");
  if (extractDates(input.text).length > 0) specificity.push("spec.date_named");
  if (parseSoleSize(input.text) !== null) specificity.push("spec.size_named");

  let timing: DmSignals["timing"] = null;
  if (input.shopTimeZone !== null) {
    const b = attributionTimeBuckets(input.sentAt, input.shopTimeZone);
    timing = { dayPart: b.dayPart, weekend: b.dayType === "weekend" };
  }

  return { specificity, timing, entryKind: entryKindOf(input.channel, input.entrySource) };
}

/**
 * Comment channels are comments. A DM thread opened from a post, reel or story
 * (an entry source is recorded) is a story reply, the webhook parser's kind for
 * it. No entry source is a plain message. A channel this does not know is
 * null, never guessed.
 */
export function entryKindOf(channel: string, entrySource: string | null): EntryKind | null {
  if (channel === "instagram_comment" || channel === "facebook_comment") return "comment";
  if (channel === "instagram" || channel === "facebook") return entrySource !== null && entrySource !== "" ? "story_reply" : "message";
  if (channel === "sms" || channel === "email" || channel === "webchat") return "message";
  return null;
}
